import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { markSolved, markUnsolved } from "../services/api";

const getRatingTier = (rating) => {
  if (!rating) return null;
  if (rating < 1600) return { color: "#00b8a3", bg: "rgba(0,184,163,0.1)",  border: "rgba(0,184,163,0.28)"  };
  if (rating < 2000) return { color: "#ffc01e", bg: "rgba(255,192,30,0.1)",  border: "rgba(255,192,30,0.28)"  };
  return                     { color: "#ff375f", bg: "rgba(255,55,95,0.1)",  border: "rgba(255,55,95,0.28)" };
};

const SkeletonRow = () => (
  <div className="p-4 flex items-center gap-3 animate-pulse">
    <div className="w-5 h-3 rounded bg-white/8 flex-shrink-0" />
    <div className="w-5 h-5 rounded-md bg-white/8 flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 bg-white/8 rounded-md w-3/5" />
      <div className="h-2.5 bg-white/5 rounded-md w-2/5" />
    </div>
    <div className="h-5 w-14 rounded-md bg-white/8 flex-shrink-0" />
  </div>
);

function ProblemList({ problems, userId, onSolved, onUnsolved, mode, loading, startIndex = 0, hideRating = false }) {
  const [loadingSlug, setLoadingSlug] = useState(null);

  if (loading) {
    return (
      <div className="space-y-1">
        {[...Array(8)].map((_, i) => <SkeletonRow key={i} />)}
      </div>
    );
  }

  if (!Array.isArray(problems) || problems.length === 0) {
    return (
      <div className="glass text-center py-14 space-y-3">
        <div className="text-3xl">{mode === "solved" ? "📋" : "🎉"}</div>
        <p className="text-gray-300 font-medium text-sm">
          {mode === "solved" ? "No solved problems yet" : "All caught up!"}
        </p>
        <p className="text-xs text-gray-600">
          {mode === "solved"
            ? "Mark problems as solved to track your progress"
            : "No unsolved problems in this category"}
        </p>
      </div>
    );
  }

  const handleSolved = async (slug) => {
    if (!userId) { alert("Please login first"); return; }
    try {
      setLoadingSlug(slug);
      await markSolved(userId, slug);
      onSolved(slug);
    } catch (err) { console.error(err); }
    finally { setLoadingSlug(null); }
  };

  const handleUnsolved = async (slug) => {
    if (!userId) { alert("Please login first"); return; }
    try {
      setLoadingSlug(slug);
      await markUnsolved(userId, slug);
      onUnsolved(slug);
    } catch (err) { console.error(err); }
    finally { setLoadingSlug(null); }
  };

  const isSolved = mode === "solved";

  return (
    <div className="space-y-1.5">
      {problems.map((p, i) => {
        const isLoading = loadingSlug === p.title_slug;
        const tier = getRatingTier(p.rating);
        const rowNum = startIndex + i + 1;
        const CONTEST_DURATION_MS = 90 * 60 * 1000; // LeetCode contests are always 90 min
        const now = Date.now();
        const startMs = p.contest_start_time != null ? p.contest_start_time * 1000 : null;
        const isUpcoming = startMs != null && startMs > now;
        const isLive = startMs != null && startMs <= now && (startMs + CONTEST_DURATION_MS) > now;
        const isLocked = isUpcoming || isLive;

        return (
          <div
            key={p.title_slug}
            className="group relative px-4 py-3 flex items-center gap-3 transition-all duration-200"
            style={{
              background: isSolved
                ? "rgba(0,184,163,0.07)"
                : "rgba(15,23,42,0.7)",
              border: isSolved
                ? "1px solid rgba(0,184,163,0.2)"
                : "1px solid rgba(255,255,255,0.07)",
              borderRadius: 12,
            }}
            onMouseEnter={e => {
              if (!isSolved) e.currentTarget.style.background = "rgba(30,41,59,0.8)";
            }}
            onMouseLeave={e => {
              if (!isSolved) e.currentTarget.style.background = "rgba(15,23,42,0.7)";
            }}
          >
            {/* left accent bar */}
            <div className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-full transition-all duration-200
              ${isSolved
                ? "bg-green-400/35 group-hover:bg-green-400/60"
                : "bg-transparent group-hover:bg-blue-400/45"}`}
            />

            {/* row number */}
            <span className="text-[11px] text-gray-700 w-5 text-right flex-shrink-0 select-none font-mono group-hover:text-gray-500 transition-colors">
              {rowNum}
            </span>

            {/* checkbox */}
            <button
              onClick={() => isSolved ? handleUnsolved(p.title_slug) : handleSolved(p.title_slug)}
              disabled={isLoading || isLocked}
              title={isUpcoming ? "Contest not yet started" : isLive ? "Contest in progress" : isSolved ? "Mark as unsolved" : "Mark as solved"}
              className={`w-5 h-5 flex-shrink-0 rounded-md border flex items-center justify-center
                transition-all duration-200
                ${isLocked
                  ? "border-gray-700 bg-transparent opacity-30 cursor-not-allowed"
                  : `cursor-pointer ${isSolved
                      ? "border-green-400 bg-green-500/20 shadow-[0_0_8px_rgba(34,197,94,0.25)]"
                      : "border-gray-600 hover:border-green-400 hover:bg-green-500/10"
                    }`
                }
                ${isLoading ? "opacity-40 cursor-wait" : ""}
              `}
            >
              <span className={`text-green-400 text-[11px] transition-all duration-150
                ${isSolved && !isLocked
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-50 group-hover:opacity-50 group-hover:scale-100"}`}>
                ✓
              </span>
            </button>

            {/* title + contest */}
            <div className="flex-1 min-w-0">
              {isLocked ? (
                <span className="inline-flex items-center gap-1.5 max-w-full cursor-not-allowed">
                  <span className="text-sm font-medium text-gray-500 truncate">
                    {p.title}
                  </span>
                </span>
              ) : (
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 max-w-full group/link"
                >
                  <span className="text-sm font-medium text-gray-200 truncate group-hover/link:text-blue-400 transition-colors duration-150">
                    {p.title}
                  </span>
                  <ExternalLink
                    size={11}
                    className="flex-shrink-0 text-gray-600 opacity-0 group-hover/link:opacity-100 transition-opacity"
                  />
                </a>
              )}
              {p.contest_title && (
                <p className="text-[11px] text-gray-600 truncate mt-0.5 group-hover:text-gray-500 transition-colors">
                  {p.contest_title}
                </p>
              )}
            </div>

            {/* rating */}
            {!hideRating && (
              <div className="flex-shrink-0">
                {isUpcoming ? (
                  <span style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 8, background: "rgba(168,85,247,0.1)", color: "rgba(168,85,247,0.8)", border: "1px solid rgba(168,85,247,0.3)", fontWeight: 600 }}>
                    Upcoming
                  </span>
                ) : isLive ? (
                  <span style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 8, background: "rgba(234,179,8,0.1)", color: "rgba(234,179,8,0.9)", border: "1px solid rgba(234,179,8,0.3)", fontWeight: 600 }}>
                    Live
                  </span>
                ) : tier ? (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 9px",
                      borderRadius: 8,
                      background: tier.bg,
                      border: `1px solid ${tier.border}`,
                      color: tier.color,
                      fontSize: 11.5,
                      fontWeight: 600,
                      fontFamily: "var(--font-display)",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {Math.round(p.rating)}
                  </span>
                ) : (
                  <span style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 8, background: "rgba(255,255,255,0.04)", color: "rgba(100,116,139,0.5)", border: "1px solid rgba(255,255,255,0.07)" }}>
                    N/A
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ProblemList;
